import React, { useState, useEffect } from 'react';
import { Button } from './button';
import { Card, CardContent } from './card';
import { Badge } from './badge';
import { Separator } from './separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './dialog';
import { ScrollArea } from './scroll-area';
import { 
  Bug, 
  Copy, 
  Download, 
  Eye, 
  Trash2, 
  User, 
  Clock,
  Globe
} from 'lucide-react';
import { errorService } from '../../shared/lib/error-service';
import { type ProcessedError, ErrorType, ErrorSeverity } from '../../shared/types/error-types';
import { toast } from 'sonner';

interface ErrorReporterProps {
  className?: string;
}

export const ErrorReporter: React.FC<ErrorReporterProps> = ({ className }) => {
  const [errors, setErrors] = useState<ProcessedError[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedError, setSelectedError] = useState<ProcessedError | null>(null);

  useEffect(() => {
    // Load errors when component mounts
    setErrors(errorService.getErrorQueue());
    
    // Set up interval to refresh errors (in case new ones are added)
    const interval = setInterval(() => {
      setErrors(errorService.getErrorQueue());
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  const handleClearErrors = () => {
    errorService.clearErrorQueue();
    setErrors([]);
    toast.success('Error log cleared');
  };

  const handleExportErrors = () => {
    const errorData = errorService.exportErrors();
    const blob = new Blob([errorData], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `error-log-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('Error log exported');
  };

  const handleCopyError = async (error: ProcessedError) => {
    try {
      await navigator.clipboard.writeText(JSON.stringify({
        id: error.id,
        timestamp: error.timestamp,
        type: error.type,
        severity: error.severity,
        message: error.technicalMessage,
        context: error.context,
      }, null, 2));
      toast.success('Error details copied to clipboard');
    } catch {
      toast.error('Failed to copy error details');
    }
  };

  const getErrorTypeIcon = (type: ErrorType) => {
    switch (type) {
      case ErrorType.NETWORK:
        return <Globe className="h-4 w-4" />;
      case ErrorType.AUTHENTICATION:
      case ErrorType.AUTHORIZATION:
        return <User className="h-4 w-4" />;
      default:
        return <Bug className="h-4 w-4" />;
    }
  };

  const getErrorTypeColor = (type: ErrorType) => {
    switch (type) {
      case ErrorType.NETWORK:
        return 'bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-400';
      case ErrorType.AUTHENTICATION:
      case ErrorType.AUTHORIZATION:
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400';
      case ErrorType.SERVER:
        return 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400';
      case ErrorType.VALIDATION:
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400';
    }
  };

  const getSeverityColor = (severity: ErrorSeverity) => {
    switch (severity) {
      case ErrorSeverity.CRITICAL:
        return 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400';
      case ErrorSeverity.HIGH:
        return 'bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-400';
      case ErrorSeverity.MEDIUM:
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400';
      case ErrorSeverity.LOW:
        return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400';
    }
  };

  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleString();
  };

  // Only show in development mode
  if (import.meta.env.PROD) {
    return null;
  }

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className={`fixed bottom-4 right-4 z-50 ${className}`}
        >
          <Bug className="h-4 w-4 mr-2" />
          Errors ({errors.length})
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-4xl max-h-[80vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bug className="h-5 w-5" />
            Error Reporter
            <Badge variant="outline">{errors.length} errors</Badge>
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          <div className="flex gap-2">
            <Button onClick={handleExportErrors} variant="outline" size="sm">
              <Download className="h-4 w-4 mr-2" />
              Export
            </Button>
            <Button onClick={handleClearErrors} variant="outline" size="sm">
              <Trash2 className="h-4 w-4 mr-2" />
              Clear All
            </Button>
          </div>

          <ScrollArea className="h-[500px]">
            <div className="space-y-3">
              {errors.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Bug className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p>No errors recorded</p>
                </div>
              ) : (
                errors.map((error) => (
                  <Card key={error.id} className="cursor-pointer hover:bg-muted/50">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-2">
                            <Badge className={getErrorTypeColor(error.type)}>
                              {getErrorTypeIcon(error.type)}
                              <span className="ml-1">{error.type}</span>
                            </Badge>
                            <Badge variant="outline" className={getSeverityColor(error.severity)}>
                              {error.severity}
                            </Badge>
                            <div className="flex items-center gap-1 text-xs text-muted-foreground">
                              <Clock className="h-3 w-3" />
                              {formatTimestamp(error.timestamp)}
                            </div>
                          </div>
                          
                          <p className="text-sm font-medium mb-1">{error.userMessage}</p>
                          <p className="text-xs text-muted-foreground font-mono">
                            {error.technicalMessage}
                          </p>
                          
                          {error.context && (
                            <div className="mt-2 text-xs text-muted-foreground">
                              {error.context.component && (
                                <span>Component: {error.context.component}</span>
                              )}
                              {error.context.action && (
                                <span className="ml-2">Action: {error.context.action}</span>
                              )}
                            </div>
                          )}
                        </div>
                        
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedError(error);
                            }}
                          >
                            <Eye className="h-3 w-3" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCopyError(error);
                            }}
                          >
                            <Copy className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </ScrollArea>
        </div>
      </DialogContent>

      {/* Error Detail Dialog */}
      <Dialog open={!!selectedError} onOpenChange={() => setSelectedError(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bug className="h-5 w-5" />
              Error Details
              <Badge variant="outline">{selectedError?.id}</Badge>
            </DialogTitle>
          </DialogHeader>
          
          {selectedError && (
            <ScrollArea className="h-[500px]">
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium">Type</label>
                    <Badge className={getErrorTypeColor(selectedError.type)}>
                      {getErrorTypeIcon(selectedError.type)}
                      <span className="ml-1">{selectedError.type}</span>
                    </Badge>
                  </div>
                  <div>
                    <label className="text-sm font-medium">Severity</label>
                    <Badge variant="outline" className={getSeverityColor(selectedError.severity)}>
                      {selectedError.severity}
                    </Badge>
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium">Timestamp</label>
                  <p className="text-sm text-muted-foreground">
                    {formatTimestamp(selectedError.timestamp)}
                  </p>
                </div>

                <Separator />

                <div>
                  <label className="text-sm font-medium">User Message</label>
                  <p className="text-sm">{selectedError.userMessage}</p>
                </div>

                <div>
                  <label className="text-sm font-medium">Technical Message</label>
                  <p className="text-sm font-mono bg-muted p-2 rounded">
                    {selectedError.technicalMessage}
                  </p>
                </div>

                {selectedError.suggestions.length > 0 && (
                  <div>
                    <label className="text-sm font-medium">Suggestions</label>
                    <ul className="text-sm text-muted-foreground space-y-1">
                      {selectedError.suggestions.map((suggestion: string, index: number) => (
                        <li key={index}>• {suggestion}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {selectedError.context && (
                  <div>
                    <label className="text-sm font-medium">Context</label>
                    <pre className="text-xs bg-muted p-2 rounded overflow-auto">
                      {JSON.stringify(selectedError.context, null, 2)}
                    </pre>
                  </div>
                )}

                <div>
                  <label className="text-sm font-medium">Original Error</label>
                  <pre className="text-xs bg-muted p-2 rounded overflow-auto max-h-32">
                    {typeof selectedError.originalError === 'string' 
                      ? selectedError.originalError
                      : JSON.stringify(selectedError.originalError, null, 2)
                    }
                  </pre>
                </div>

                <div className="flex gap-2">
                  <Button
                    onClick={() => handleCopyError(selectedError)}
                    variant="outline"
                    size="sm"
                  >
                    <Copy className="h-4 w-4 mr-2" />
                    Copy Details
                  </Button>
                </div>
              </div>
            </ScrollArea>
          )}
        </DialogContent>
      </Dialog>
    </Dialog>
  );
};